import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { dt } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { Bell, AlertTriangle, Info, CheckCheck, BellOff } from "lucide-react";

const LEVEL = {
  warning: { icon: AlertTriangle, cls: "text-warning" },
  info: { icon: Info, cls: "text-primary" },
};

export const NotificationBell = () => {
  const [data, setData] = useState({ notifications: [], unread: 0 });
  const [open, setOpen] = useState(false);
  const timer = useRef(null);

  const load = () => api.get("/notifications").then((r) => setData(r.data)).catch(() => {});

  useEffect(() => {
    load();
    timer.current = setInterval(load, 30000);
    return () => clearInterval(timer.current);
  }, []);

  const markAll = async () => {
    await api.post("/notifications/read-all", {});
    load();
  };

  const onOpen = (o) => {
    setOpen(o);
    if (o && data.unread > 0) {
      // mark individual as read lazily when opened
      api.post("/notifications/read-all", {}).then(load);
    }
  };

  const { notifications, unread } = data;

  return (
    <Popover open={open} onOpenChange={onOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="icon" className="relative rounded-full" data-testid="notification-bell">
          <Bell className="h-4 w-4" />
          {unread > 0 && (
            <span
              data-testid="notification-unread-badge"
              className="absolute -top-1 -right-1 h-4 min-w-4 px-1 rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold grid place-items-center"
            >
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0" data-testid="notification-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <p className="font-heading font-semibold text-sm">Alerts</p>
          <Button variant="ghost" size="sm" className="h-7 text-xs text-muted-foreground" onClick={markAll} data-testid="notification-mark-all">
            <CheckCheck className="h-3.5 w-3.5 mr-1" /> Mark all read
          </Button>
        </div>
        <div className="max-h-96 overflow-y-auto">
          {notifications.length === 0 ? (
            <div className="py-10 text-center text-sm text-muted-foreground flex flex-col items-center gap-2">
              <BellOff className="h-5 w-5" /> No alerts yet
            </div>
          ) : (
            notifications.map((n) => {
              const L = LEVEL[n.level] || LEVEL.info;
              return (
                <div key={n.id} data-testid={`notification-${n.id}`}
                  className={`flex gap-3 px-4 py-3 border-b last:border-0 ${n.read ? "" : "bg-primary/[0.03]"}`}>
                  <L.icon className={`h-4 w-4 mt-0.5 shrink-0 ${L.cls}`} />
                  <div className="min-w-0">
                    <p className="text-sm font-medium">{n.title}</p>
                    <p className="text-xs text-muted-foreground break-words">{n.message}</p>
                    <p className="text-[10px] text-muted-foreground mt-1 font-mono">{dt(n.created_at)}</p>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
